/**
 * ゲートクローズ (GC) の境界を扱う画面共通の module。
 *
 * GC 済みかどうかの判定は backend の `valueobject.GateCloseFrontier` だけが持つ
 * (docs/adr/gate-close-frontier-single-source.md)。backend は応答を作った時刻の境界を
 * bffuser.v1.GateCloseFrontier (= まだ GC を迎えていない最初のコマと、その GC 時刻) で
 * 返し、frontend はコマをその 1 点と比べるだけでロックを判定する。GC の余裕時間
 * (コマ開始の 1 時間前) と日跨ぎ (前日 23:00 / 23:30 に翌日のコマ 1 / 2 が GC 済み) は
 * この module にも呼び出し側にも書かない。「今日」と比べる判定も書かない。
 *
 * 画面を開いたまま時間が進む表示 (カウントダウン・チャートの GC 線) は
 * [[advanceGateCloseFrontier]] で snapshot を前に進める。前進の規則 (GC は 30 分ごとに
 * 1 コマずつ進む) は backend の写しなので、testdata/golden/gate-close-frontier/ を
 * Go と TS の両テストで読んで固定している (gateClose.golden.test.ts)。
 */
import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { GateCloseFrontier as ProtoGateCloseFrontier } from '@ebalance/proto/bffuser/v1/gate_close_model_pb'

import { KOMA_COUNT } from '~/constants/koma'
import type { CivilDate } from '~/models/civilDate'
import { compareCivilDate, protoDateToCivilDate } from '~/models/civilDate'
import { now as serverNow } from '~/models/clock'

/**
 * ある時刻の GC 境界。`nextCloseDate` / `nextCloseKoma` より前のコマ (受渡日→コマ番号の
 * 順) はすべて GC 済み、それ以降はすべて未済。
 *
 * `nextCloseDate` は「今日」ではない (22:30 JST 以降は翌日になる)。今日の代わりに使わない。
 */
export type GateCloseFrontier = {
  /** まだ GC を迎えていない最初のコマの受渡日 */
  readonly nextCloseDate: CivilDate
  /** そのコマ番号 (1-48) */
  readonly nextCloseKoma: number
  /** そのコマが GC を迎える時刻。この時刻ちょうどで GC 済みになる */
  readonly nextCloseAt: Date
}

/** GC 時刻どうしの間隔 (コマの長さ)。日跨ぎでも変わらない (JST に DST は無い)。 */
const GATE_CLOSE_INTERVAL_MS = 30 * 60 * 1000

/**
 * BFF の GateCloseFrontier を画面の型にする。未設定 (旧 backend・取得前) や欠けた
 * field を含む時は undefined を返し、呼び出し側は「GC の情報が無い」として扱う。
 */
export function gateCloseFrontierFromProto(
  proto: ProtoGateCloseFrontier | undefined,
): GateCloseFrontier | undefined {
  if (proto === undefined) return undefined
  const nextCloseDate = protoDateToCivilDate(proto.nextCloseDate)
  if (
    nextCloseDate === undefined ||
    proto.nextCloseAt === undefined ||
    proto.nextCloseKoma < 1 ||
    proto.nextCloseKoma > KOMA_COUNT
  ) {
    return undefined
  }
  return {
    nextCloseDate,
    nextCloseKoma: proto.nextCloseKoma,
    nextCloseAt: timestampDate(proto.nextCloseAt),
  }
}

/**
 * 受渡日 `date` のコマ `koma` が `frontier` の時点で GC 済み (編集不可) か。
 * frontier・date が無い時は GC で縛らない (false)。
 */
export function isKomaGateClosed(
  frontier: GateCloseFrontier | undefined,
  date: CivilDate | undefined,
  koma: number,
): boolean {
  if (frontier === undefined || date === undefined) return false
  const cmp = compareCivilDate(date, frontier.nextCloseDate)
  if (cmp !== 0) return cmp < 0
  return koma < frontier.nextCloseKoma
}

/**
 * snapshot を `now` の境界まで前に進める。`now` が次の GC 時刻に達していなければ
 * そのまま返す (時計が snapshot より遅れていても戻さない)。
 *
 * GC は 30 分ごとに 1 コマずつ進み、コマ 48 の次は翌日のコマ 1 (backend
 * `GateCloseFrontierAt(now)` と一致することを golden で固定)。
 */
export function advanceGateCloseFrontier(
  frontier: GateCloseFrontier,
  now: Date = serverNow(),
): GateCloseFrontier {
  const elapsedMs = now.getTime() - frontier.nextCloseAt.getTime()
  if (elapsedMs < 0) return frontier
  const steps = Math.floor(elapsedMs / GATE_CLOSE_INTERVAL_MS) + 1
  const komaIndex = frontier.nextCloseKoma - 1 + steps
  return {
    nextCloseDate: addCivilDays(
      frontier.nextCloseDate,
      Math.floor(komaIndex / KOMA_COUNT),
    ),
    nextCloseKoma: (komaIndex % KOMA_COUNT) + 1,
    nextCloseAt: new Date(
      frontier.nextCloseAt.getTime() + steps * GATE_CLOSE_INTERVAL_MS,
    ),
  }
}

/**
 * 受渡日 1 日分の GC カウントダウンの状態。
 * - `counting`: その日のコマ `koma` が次に GC を迎える (`closeAt` まで残り `remainingSeconds` 秒)
 * - `allClosed`: その日のコマはすべて GC 済み
 * - `notStarted`: 次の GC はその日より前の日のコマ (その日のコマはまだ 1 つも次の GC ではない)
 */
export type GateCloseCountdown =
  | {
      readonly status: 'counting'
      readonly koma: number
      readonly closeAt: Date
      readonly remainingSeconds: number
    }
  | { readonly status: 'allClosed' }
  | { readonly status: 'notStarted' }

/**
 * 受渡日 `date` について、`now` 時点で次に GC を迎えるコマと残り秒数を返す。
 * `frontier` は応答の snapshot をそのまま渡してよい (内部で `now` まで進める)。
 * 残り秒数の表示整形は `~/models/gcRemainingTime` の formatGcRemainingTime。
 */
export function gateCloseCountdownOn(
  frontier: GateCloseFrontier,
  date: CivilDate,
  now: Date = serverNow(),
): GateCloseCountdown {
  const current = advanceGateCloseFrontier(frontier, now)
  const cmp = compareCivilDate(date, current.nextCloseDate)
  if (cmp < 0) return { status: 'allClosed' }
  if (cmp > 0) return { status: 'notStarted' }
  return {
    status: 'counting',
    koma: current.nextCloseKoma,
    closeAt: current.nextCloseAt,
    remainingSeconds: Math.floor(
      (current.nextCloseAt.getTime() - now.getTime()) / 1000,
    ),
  }
}

function addCivilDays(date: CivilDate, days: number): CivilDate {
  if (days === 0) return date
  // 暦日だけを UTC 上で数える (端末 TZ を読まない)。
  const d = new Date(Date.UTC(date.year, date.month - 1, date.day + days))
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
  }
}
